// Mochi's idle micro-glances — pupil-only sideways shifts that fire when
// the user has been inactive for ≥ 30s and Mochi is in an idle-compatible
// mood (happy / cozy / proud / excited / determined).
//
// Returns the current pupil offset { x, y } in SVG units. Avatar adds this
// to the existing cursor-tracking gaze (so the two stack naturally).
//
// Design constraints from the spec:
//   - rAF-driven (no setInterval) so the loop pauses with the tab.
//   - Pauses entirely while document.hidden.
//   - 200ms ease-in → 600-900ms hold → 200ms ease-out → blink → idle
//   - Next glance every 8-15s while idle persists.
//   - 20% chance of a double-glance (one direction → centre → other direction).
//   - Skips silently if avg frame interval > 20ms (i.e. < 50fps).
//
// Activity events watched: mousemove, touchstart, keydown, scroll.
// Any of these resets the idle timer and cancels in-flight glances.

import { useEffect, useRef, useState } from "react";

const IDLE_THRESHOLD_MS = 30_000;
const MIN_INTERVAL_MS = 8_000;
const MAX_INTERVAL_MS = 15_000;
const HOLD_MIN_MS = 600;
const HOLD_MAX_MS = 900;
const FADE_MS = 200;
const PUPIL_OFFSET = 3.5;   // SVG units — clamped to a small, kawaii-safe radius
const DOUBLE_GLANCE_CHANCE = 0.2;
const DOUBLE_PAUSE_MS = 400;
const SLOW_FRAME_THRESHOLD_MS = 20;  // 50fps gate — skip glance if breached
const FPS_SAMPLE_FRAMES = 30;

const ACTIVITY_EVENTS = ["mousemove", "touchstart", "keydown", "scroll"];

export function useIdleGlance({ active }) {
    const [offset, setOffset] = useState({ x: 0, y: 0 });

    // Latest activity ts, scheduled-glance ts, and active-execution lock all
    // live in refs so the rAF loop can read them without re-rendering.
    const lastActivityRef = useRef(performance.now());
    const nextGlanceAtRef = useRef(0);     // 0 = "as soon as threshold met"
    const executingRef = useRef(false);
    const rafIdRef = useRef(0);
    const timeoutsRef = useRef([]);
    const lastFrameTsRef = useRef(0);
    const frameDeltasRef = useRef([]);

    // ── single document-level activity listener ──────────────────────────
    useEffect(() => {
        const onActivity = () => {
            lastActivityRef.current = performance.now();
            nextGlanceAtRef.current = 0;
            // Cancel any in-flight glance — user is back.
            timeoutsRef.current.forEach((t) => clearTimeout(t));
            timeoutsRef.current = [];
            executingRef.current = false;
            // Snap pupils to centre. The 0→0 set is a no-op when already
            // centred, but cheap and correct when we interrupt mid-glance.
            setOffset((cur) => (cur.x === 0 && cur.y === 0 ? cur : { x: 0, y: 0 }));
        };
        ACTIVITY_EVENTS.forEach((e) =>
            document.addEventListener(e, onActivity, { passive: true }),
        );
        return () => {
            ACTIVITY_EVENTS.forEach((e) =>
                document.removeEventListener(e, onActivity),
            );
        };
    }, []);

    // ── rAF loop + glance scheduler ──────────────────────────────────────
    useEffect(() => {
        if (!active) {
            // Mood/UI says glances aren't appropriate right now — clean up
            // everything and snap to centre.
            timeoutsRef.current.forEach((t) => clearTimeout(t));
            timeoutsRef.current = [];
            executingRef.current = false;
            setOffset((cur) => (cur.x === 0 && cur.y === 0 ? cur : { x: 0, y: 0 }));
            return;
        }

        function executeGlance() {
            executingRef.current = true;
            const dir = Math.random() < 0.5 ? -1 : 1;
            const isDouble = Math.random() < DOUBLE_GLANCE_CHANCE;
            const hold = HOLD_MIN_MS + Math.random() * (HOLD_MAX_MS - HOLD_MIN_MS);

            // First glance: jump to direction (framer will ease the transform).
            setOffset({ x: dir * PUPIL_OFFSET, y: 0 });

            // Return to centre after the hold.
            const t1 = setTimeout(() => {
                setOffset({ x: 0, y: 0 });
            }, FADE_MS + hold);
            timeoutsRef.current.push(t1);

            // Total sequence duration; double-glance adds a second half.
            let totalMs = FADE_MS + hold + FADE_MS;
            if (isDouble) {
                const t2 = setTimeout(() => {
                    setOffset({ x: -dir * PUPIL_OFFSET, y: 0 });
                }, totalMs + DOUBLE_PAUSE_MS);
                const t3 = setTimeout(() => {
                    setOffset({ x: 0, y: 0 });
                }, totalMs + DOUBLE_PAUSE_MS + FADE_MS + hold);
                timeoutsRef.current.push(t2, t3);
                totalMs += DOUBLE_PAUSE_MS + FADE_MS + hold + FADE_MS;
            }

            // After sequence: clear lock + schedule next.
            const doneT = setTimeout(() => {
                executingRef.current = false;
                const wait = MIN_INTERVAL_MS + Math.random() * (MAX_INTERVAL_MS - MIN_INTERVAL_MS);
                nextGlanceAtRef.current = performance.now() + wait;
            }, totalMs);
            timeoutsRef.current.push(doneT);
        }

        function frameOk() {
            // Returns true if recent frame intervals are smooth enough.
            // Empty buffer → assume fine; otherwise compare avg vs threshold.
            const buf = frameDeltasRef.current;
            if (buf.length < FPS_SAMPLE_FRAMES) return true;
            const sum = buf.reduce((a, b) => a + b, 0);
            return (sum / buf.length) <= SLOW_FRAME_THRESHOLD_MS;
        }

        function loop(ts) {
            rafIdRef.current = 0;

            // Track frame delta for the FPS gate (rolling window).
            if (lastFrameTsRef.current) {
                const delta = ts - lastFrameTsRef.current;
                frameDeltasRef.current.push(delta);
                if (frameDeltasRef.current.length > FPS_SAMPLE_FRAMES) {
                    frameDeltasRef.current.shift();
                }
            }
            lastFrameTsRef.current = ts;

            if (document.hidden) {
                // Don't re-arm rAF while hidden — visibilitychange will resume.
                return;
            }

            const now = performance.now();
            const idleFor = now - lastActivityRef.current;
            if (!executingRef.current && idleFor >= IDLE_THRESHOLD_MS) {
                if (nextGlanceAtRef.current === 0) {
                    nextGlanceAtRef.current = now;
                }
                if (now >= nextGlanceAtRef.current && frameOk()) {
                    executeGlance();
                } else if (now >= nextGlanceAtRef.current && !frameOk()) {
                    // Skip silently on low-powered devices: bump the slot
                    // forward by one interval and try again later.
                    nextGlanceAtRef.current = now + MIN_INTERVAL_MS;
                }
            }
            rafIdRef.current = requestAnimationFrame(loop);
        }

        rafIdRef.current = requestAnimationFrame(loop);

        const onVisibility = () => {
            if (!document.hidden && rafIdRef.current === 0) {
                lastFrameTsRef.current = 0;
                frameDeltasRef.current = [];
                rafIdRef.current = requestAnimationFrame(loop);
            }
            if (document.hidden && rafIdRef.current) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = 0;
            }
        };
        document.addEventListener("visibilitychange", onVisibility);

        return () => {
            if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = 0;
            document.removeEventListener("visibilitychange", onVisibility);
            timeoutsRef.current.forEach((t) => clearTimeout(t));
            timeoutsRef.current = [];
            executingRef.current = false;
            setOffset((cur) => (cur.x === 0 && cur.y === 0 ? cur : { x: 0, y: 0 }));
        };
    }, [active]);

    return offset;
}

// Moods where idle glances are SUPPRESSED. Per spec: party + worried, plus
// the natural "loud" cousins (hype) and any mood that mid-animates the eyes
// itself (wink, frustrated). Everything else — happy, cozy, proud, excited,
// determined, sleepy, sad — gets glances so Mochi feels alive in ALL quiet
// states, not just the bright ones.
export const IDLE_INCOMPATIBLE_MOODS = new Set([
    "party", "hype", "worried", "frustrated", "wink",
]);

// Back-compat export kept for any callers that imported the ALLOW set directly.
// The host code uses `IDLE_INCOMPATIBLE_MOODS` going forward via `isIdleCompatible`.
export const IDLE_COMPATIBLE_MOODS = new Set([
    "happy", "cozy", "proud", "excited", "determined", "sleepy", "sad",
]);

export function isIdleCompatible(mood) {
    return !!mood && !IDLE_INCOMPATIBLE_MOODS.has(mood);
}
